/**
 * <TaxReserveWidget> — clinician dashboard tile (Feb 2026).
 *
 * Backs the pitch claim "we automatically set aside money for taxes
 * + show estimated quarterlies" by surfacing the tax-reserve balance,
 * YTD set-aside, next IRS due date, and computed quarterly estimate.
 *
 * NOT a tax preparer. The disclaimer from the API is always rendered
 * inline so we cannot accidentally cross the preparer line. The
 * shortfall banner shows red ONLY when the estimate exceeds the
 * currently set-aside balance — honest framing for the user.
 *
 * Reads two endpoints:
 *   GET /api/clinician/tax/reserve     → balance, %, next due, recent
 *   GET /api/clinician/tax/estimated   → SE + income tax estimate
 */
import React, { useEffect, useState } from 'react';
import axios from '../../lib/api';
import { Wallet, CalendarClock, AlertCircle, TrendingUp, ArrowRight } from 'lucide-react';
import { TileHeader } from './BentoWidgets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso || '—'; }
};

export const TaxReserveWidget = ({ getAuthHeader }) => {
  const [loading, setLoading] = useState(true);
  const [reserve, setReserve] = useState(null);
  const [est, setEst] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/clinician/tax/reserve`, getAuthHeader?.()),
          axios.get(`${API}/clinician/tax/estimated`, getAuthHeader?.()),
        ]);
        if (active) { setReserve(r1.data); setEst(r2.data); }
      } catch (e) {
        if (active) setErr(e?.response?.data?.detail || 'Unable to load tax reserve.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [getAuthHeader]);

  if (loading) {
    return (
      <div data-testid="tax-reserve-widget">
        <TileHeader eyebrow="Taxes" title="Tax reserve & quarterly estimate" />
        <div className="mt-5 h-32 rounded-xl bg-cn-bg/60 border border-cn-border/60 animate-pulse" />
      </div>
    );
  }
  if (err) {
    return (
      <div data-testid="tax-reserve-widget">
        <TileHeader eyebrow="Taxes" title="Tax reserve & quarterly estimate" />
        <p className="text-xs text-cn-text-muted mt-4">{err}</p>
      </div>
    );
  }

  const shortfall = Number(est?.shortfall_vs_estimate || 0);
  const reservePct = Number(reserve?.reserve_pct || 0);

  return (
    <div data-testid="tax-reserve-widget">
      <TileHeader
        eyebrow="Taxes"
        title="Tax reserve & quarterly estimate"
        action={
          <a
            href="/clinician/banking"
            className="text-sm font-semibold text-cn-primary inline-flex items-center gap-1.5 hover:underline"
            data-testid="tax-reserve-widget-settings"
          >
            Adjust <ArrowRight className="w-4 h-4" />
          </a>
        }
      />

      {/* Big-number row */}
      <div className="grid grid-cols-2 gap-3 mt-5">
        <div className="px-4 py-4 rounded-2xl border border-cn-primary/30 bg-cn-primary/8">
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="w-4 h-4 text-cn-primary" />
            <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">Set aside</p>
          </div>
          <p className="text-2xl font-display font-extrabold text-cn-text dark:text-white"
             data-testid="tax-reserve-balance">{fmtUSD(reserve?.set_aside_balance)}</p>
          <p className="text-[11px] text-cn-text-muted mt-1">
            YTD reserved {fmtUSD(reserve?.ytd_reserved)} · at {Math.round(reservePct * 100)}%
          </p>
        </div>
        <div className="px-4 py-4 rounded-2xl border border-cn-border/70 bg-cn-bg/60">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-cn-text-muted" />
            <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">Est. this quarter</p>
          </div>
          <p className="text-2xl font-display font-extrabold text-cn-text dark:text-white"
             data-testid="tax-reserve-quarter-estimate">{fmtUSD(est?.per_quarter_est)}</p>
          <p className="text-[11px] text-cn-text-muted mt-1">
            SE {fmtUSD(est?.self_employment_tax_est)} + income {fmtUSD(est?.income_tax_est)}
          </p>
        </div>
      </div>

      {/* Next-due row */}
      <div className="mt-3 p-3 rounded-xl border border-cn-border/60 bg-white dark:bg-cn-ink-800 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <CalendarClock className="w-4 h-4 text-cn-text-muted shrink-0" />
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-cn-text-muted font-bold">Next IRS estimated due</p>
            <p className="text-sm font-semibold text-cn-text dark:text-white truncate"
               data-testid="tax-reserve-next-due">{fmtDate(reserve?.next_estimated_due)}</p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[11px] uppercase tracking-wider text-cn-text-muted font-bold">YTD net</p>
          <p className="text-sm font-semibold text-cn-text dark:text-white">{fmtUSD(est?.ytd_net_earnings)}</p>
        </div>
      </div>

      {/* Shortfall banner — only when behind */}
      {shortfall > 0.01 && (
        <div className="mt-3 p-3 rounded-xl border border-cn-accent/40 bg-cn-accent/8 flex items-start gap-2"
             data-testid="tax-reserve-shortfall">
          <AlertCircle className="w-4 h-4 text-cn-accent shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="font-semibold text-cn-accent">
              You&apos;re {fmtUSD(shortfall)} short of the estimate
            </p>
            <p className="text-cn-text-muted mt-0.5">
              Bump your reserve % or add a manual set-aside to catch up before the next IRS deadline.
            </p>
          </div>
        </div>
      )}

      {/* Always-on disclaimer */}
      <p className="text-[10px] text-cn-text-muted mt-3 italic">
        {reserve?.disclaimer || est?.disclaimer || 'Estimate only — not tax advice.'}
      </p>
    </div>
  );
};

export default TaxReserveWidget;
